/**
 * 规则 — how the recommendation engine turns candidate programs into plans.
 *
 * Only the ENGINE's rules: which questionnaire answers make a program a candidate is
 * the decision tree's business, not this page's.
 *
 * Read-only, from GetRecommendationRules (Deal Desk admins only): the rules are a
 * versioned business document kept by the service. Parameters the engine reads from
 * program data (the SCE TCV threshold, the R-PPA ARR band) arrive already filled from
 * the live program table and are marked as such.
 */
import { type CSSProperties, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  ExpandableSection,
  Header,
  Select,
  type SelectProps,
  SpaceBetween,
  Spinner,
  StatusIndicator,
  Table,
} from '@amzn/awsui-components-react';
import {
  colorBackgroundLayoutMain,
  colorBackgroundStatusInfo,
  colorBorderDividerDefault,
  colorTextBodySecondary,
  colorTextStatusInactive,
  colorTextStatusError,
  colorTextStatusInfo,
} from '@amzn/awsui-design-tokens/polaris';
import { useTranslation } from 'react-i18next';

import type { RuleData, RuleSectionData, RuleVersionData } from '../../client/CpaClient';
import { useRecommendationRules } from '../../state/useRecommendationRules';
import { filterSections, renderRule, rulePrograms } from '../../utils/rules';
import RuleEditor from './RuleEditor';
import VersionControls from './VersionControls';
import './RulesPage.css';

/** Design tokens handed to RulesPage.css (Cloudscape's own custom-property names are hashed). */
const TOKEN_VARS = {
  '--cpa-live-text': colorTextStatusInfo,
  '--cpa-live-bg': colorBackgroundStatusInfo,
  '--cpa-const-bg': colorBackgroundLayoutMain,
  '--cpa-muted': colorTextStatusInactive,
  '--cpa-secondary': colorTextBodySecondary,
  '--cpa-divider': colorBorderDividerDefault,
  '--cpa-error': colorTextStatusError,
} as CSSProperties;

function RuleRow({ number, rule, highlight }: { number: string; rule: RuleData; highlight: string | null }) {
  const { t } = useTranslation('cpa');
  return (
    <li className="cpa-rules-rule">
      <span className="cpa-rules-number">{number}</span>
      <div className="cpa-rules-body">
        <div className="cpa-rules-text">
          {renderRule(rule).map((part, i) =>
            part.kind === 'text' ? (
              <span key={i}>{part.text}</span>
            ) : (
              <span
                key={i}
                className={`cpa-rules-param ${part.live ? 'is-live' : ''}`}
                title={part.live ? t('rulesParamLive', { source: part.program }) : undefined}
              >
                {part.value ?? '-'}
              </span>
            ),
          )}
        </div>
        <div className="cpa-rules-meta">
          {(rule.programs ?? []).map((name) => (
            <span key={name} className={`cpa-rules-program ${name === highlight ? 'is-match' : ''}`}>
              {name}
            </span>
          ))}
        </div>
      </div>
    </li>
  );
}

function VersionBadge({ rules }: { rules: RuleVersionData }) {
  const { t } = useTranslation('cpa');
  const sync = rules.engineSync;
  return (
    <SpaceBetween direction="horizontal" size="xs" alignItems="center">
      <span
        className="cpa-rules-version"
        title={
          rules.publishedAt
            ? t('rulesPublished', {
                date: rules.publishedAt.toLocaleDateString(),
                by: rules.publishedBy ?? '-',
              })
            : undefined
        }
      >
        {t('rulesVersion', { version: rules.version })}
      </span>
      {sync?.status === 'implemented' ? (
        <StatusIndicator type="success">
          {t('rulesEngineImplemented', { ref: sync.reference ?? '-' })}
        </StatusIndicator>
      ) : (
        <StatusIndicator type="warning">{t('rulesEnginePending')}</StatusIndicator>
      )}
    </SpaceBetween>
  );
}

function SectionTable({ section }: { section: RuleSectionData }) {
  const table = section.table;
  if (!table?.columns?.length) return null;
  return (
    <Table
      variant="embedded"
      wrapLines
      items={table.rows ?? []}
      columnDefinitions={table.columns.map((c) => ({
        id: c.columnId!,
        header: c.title,
        cell: (row: Record<string, string>) => row[c.columnId!] ?? '-',
      }))}
    />
  );
}

const RULES = '/admin-rbms/cpa/rules';

export default function RulesPage() {
  const { t } = useTranslation('cpa');
  const tx = t as unknown as (key: string) => string;
  const navigate = useNavigate();
  const [params] = useSearchParams();
  // ?version=N shows that version; otherwise the latest published one.
  const requested = Number(params.get('version'));
  const version = Number.isInteger(requested) && requested > 0 ? requested : undefined;
  const { state, reload } = useRecommendationRules(version);
  const show = (v: number | undefined) => navigate(v === undefined ? RULES : `${RULES}?version=${v}`);
  const [program, setProgram] = useState<SelectProps.Option | null>(null);
  const [editing, setEditing] = useState(false);

  const allSections = useMemo(() => (state.kind === 'ready' ? (state.rules.sections ?? []) : []), [state]);
  const selected = program?.value ?? null;
  const sections = useMemo(() => filterSections(allSections, selected), [allSections, selected]);
  const ruleCount = allSections.reduce((n, s) => n + (s.rules?.length ?? 0), 0);
  const options: SelectProps.Option[] = [
    { value: '', label: t('rulesAllPrograms') },
    ...rulePrograms(allSections).map((name) => ({ value: name, label: name })),
  ];

  if (state.kind === 'denied') {
    return <Alert type="warning">{t('programsDenied')}</Alert>;
  }
  if (state.kind === 'error') {
    return (
      <Alert
        type="error"
        header={t('rulesLoadError')}
        action={<Button onClick={reload}>{t('programsRetry')}</Button>}
      >
        {state.message}
      </Alert>
    );
  }
  if (state.kind === 'loading') {
    return (
      <Box textAlign="center" padding="xxl">
        <Spinner size="large" />
      </Box>
    );
  }

  if (editing && state.rules.status === 'draft') {
    return (
      <div className="cpa-rules" style={TOKEN_VARS}>
        <RuleEditor
          rules={state.rules}
          onCancel={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            reload();
          }}
        />
      </div>
    );
  }

  return (
    <div className="cpa-rules" style={TOKEN_VARS}>
      <SpaceBetween size="l">
        {version !== undefined && state.rules.status !== 'published' && (
          <Alert
            type="info"
            action={
              <Button onClick={() => navigate('/admin-rbms/cpa/rules')}>{t('rulesBackToCurrent')}</Button>
            }
          >
            {t('rulesViewingVersion', {
              version: state.rules.version,
              status: tx(`versionStatus_${state.rules.status}`),
            })}
          </Alert>
        )}
        <Header
          variant="h2"
          counter={`(${ruleCount})`}
          description={t('rulesCaption')}
          actions={<VersionBadge rules={state.rules} />}
        >
          {t('tabRules')}
        </Header>

        <SpaceBetween direction="horizontal" size="xs" alignItems="start">
          <VersionControls shown={state.rules.version!} onShow={show} onChanged={reload} />
          {state.rules.status === 'draft' && (
            <Button iconName="edit" onClick={() => setEditing(true)}>
              {t('editRulesStart')}
            </Button>
          )}
        </SpaceBetween>

        <div className="cpa-rules-toolbar">
          <div className="cpa-rules-filter">
            <Select
              selectedOption={program ?? options[0]}
              onChange={({ detail }) =>
                setProgram(detail.selectedOption.value ? detail.selectedOption : null)
              }
              options={options}
              filteringType="auto"
              ariaLabel={t('rulesFilter')}
            />
          </div>
          <div className="cpa-rules-legend">
            <span className="cpa-rules-param is-live">{t('rulesLegendLive')}</span>
            <span className="cpa-rules-param">{t('rulesLegendEngine')}</span>
          </div>
        </div>

        {sections.length === 0 && (
          <Box textAlign="center" color="text-body-secondary" padding="l">
            {t('rulesNoMatch')}
          </Box>
        )}

        {sections.map((section) => {
          const index = allSections.findIndex((s) => s.sectionId === section.sectionId) + 1;
          const all = allSections[index - 1].rules ?? [];
          return (
            <ExpandableSection
              key={section.sectionId}
              variant="container"
              defaultExpanded
              headerText={`${index}. ${section.title}`}
              headerCounter={`(${section.rules?.length ?? 0})`}
              headerDescription={section.summary}
            >
              <SpaceBetween size="m">
                <ol className="cpa-rules-list">
                  {(section.rules ?? []).map((rule) => (
                    <RuleRow
                      key={rule.ruleId}
                      number={`${index}.${all.findIndex((r) => r.ruleId === rule.ruleId) + 1}`}
                      rule={rule}
                      highlight={selected}
                    />
                  ))}
                </ol>
                <SectionTable section={section} />
              </SpaceBetween>
            </ExpandableSection>
          );
        })}
      </SpaceBetween>
    </div>
  );
}
